// "Inside the platform": two sample dashboards side by side — a DISC style profile and a competency assessment
// dashboard (radar vs the role's target, gaps, development focus). Illustrative data only, labelled as such.
import { T } from "./glass.jsx";

const Tag = () => <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: T.mute, border: "1px dashed rgba(13,27,54,.25)", borderRadius: 99, padding: "3px 10px", whiteSpace: "nowrap" }}>Sample · illustrative data</span>;
const Head = ({ eyebrow, title, who }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
    <div>
      <div style={{ fontSize: 9.5, letterSpacing: 2.4, fontWeight: 800, color: T.gold, textTransform: "uppercase", marginBottom: 4 }}>{eyebrow}</div>
      <div className="serif" style={{ fontSize: 22, fontWeight: 700, color: T.ink, lineHeight: 1.15 }}>{title}</div>
      <div style={{ fontSize: 11.5, color: T.mute, marginTop: 3 }}>{who}</div>
    </div>
    <Tag/>
  </div>
);
const Panel = ({ children }) => <div style={{ background: "rgba(255,255,255,.82)", border: "1px solid rgba(255,255,255,.95)", borderRadius: 14, padding: "14px 16px" }}>{children}</div>;
const Sub = ({ children }) => <div className="serif" style={{ fontSize: 14, fontWeight: 700, color: T.ink, marginBottom: 10 }}>{children}</div>;

// ── DISC ──────────────────────────────────────────────────────────────────────
const DISC = [["D", "Dominance", 54, "#d9483f"], ["I", "Influence", 84, "#e8a020"], ["S", "Steadiness", 61, "#2e9b5f"], ["C", "Conscientiousness", 47, "#1f63c6"]];
function DiscDashboard() {
  const quad = [["D", "#d9483f", "M60 60 L60 6 A54 54 0 0 0 6 60 Z", [33, 38]], ["i", "#e8a020", "M60 60 L114 60 A54 54 0 0 0 60 6 Z", [87, 38]], ["S", "#2e9b5f", "M60 60 L60 114 A54 54 0 0 0 114 60 Z", [87, 86]], ["C", "#1f63c6", "M60 60 L6 60 A54 54 0 0 0 60 114 Z", [33, 86]]];
  return (
    <div className="glass" style={{ padding: 20 }} role="group" aria-label="Sample DISC dashboard (illustrative data)">
      <Head eyebrow="DISC style profile" title="Behavioural Style" who="James Whitfield · Sales Director"/>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 18, alignItems: "center", marginBottom: 14 }}>
        <svg viewBox="0 0 120 120" width="150" height="150" role="img" aria-label="DISC wheel">
          {quad.map(([l, c, d]) => <path key={l} d={d} fill={c} stroke="#fff" strokeWidth="2"/>)}
          {quad.map(([l, , , [x, y]]) => <text key={l} x={x} y={y + 5} textAnchor="middle" className="serif" style={{ fontSize: 22, fontWeight: 700, fill: "#fff" }}>{l}</text>)}
          <circle cx="60" cy="60" r="9" fill="#fff"/><circle cx="73" cy="38" r="5" fill="#0d1b36" stroke="#fff" strokeWidth="2"/>
        </svg>
        <div style={{ display: "grid", gap: 9 }}>
          {DISC.map(([l, n, v, c]) => (
            <div key={l}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, fontWeight: 600, color: T.ink2, marginBottom: 3 }}><span><b style={{ color: c }}>{l}</b> · {n}</span><b>{v}</b></div>
              <div style={{ height: 7, borderRadius: 99, background: "#e8edf3" }}><div style={{ width: `${v}%`, height: "100%", borderRadius: 99, background: c }}/></div>
            </div>
          ))}
        </div>
      </div>
      <Panel>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <Sub>The Motivator <span style={{ color: T.faint, fontWeight: 500 }}>· D + I blend</span></Sub>
          <span style={{ fontSize: 10, color: T.mute }}>1 of 16 profiles</span>
        </div>
        <div style={{ fontSize: 11.5, color: T.mute, lineHeight: 1.7, marginBottom: 8 }}>Drives results through people and energy: inspiring, decisive and quick to build momentum.</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["Persuasive", "Decisive", "Energising", "Motivates teams"].map(x => <span key={x} style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 99, background: "#e8a02018", color: "#a56a00" }}>{x}</span>)}
        </div>
      </Panel>
      <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
        {["Profile", "Management", "Work of Leaders", "Sales", "Conflict Style", "EQ Stretch"].map(r => <span key={r} style={{ fontSize: 9.5, fontWeight: 700, padding: "4px 11px", borderRadius: 8, background: T.ink, color: "#fff" }}>{r}</span>)}
      </div>
    </div>
  );
}

// ── Competency assessment ────────────────────────────────────────────────────
const COMP = [["Communication", 90, 80], ["Ownership", 88, 85], ["Empathy", 84, 75], ["Planning", 82, 85], ["Adaptability", 78, 80], ["Strategic Thinking", 70, 85], ["Delegation", 66, 80], ["Innovation", 72, 70]];
function Radar() {
  const cx = 110, cy = 110, R = 80, n = COMP.length;
  const pt = (i, v) => { const a = -Math.PI / 2 + (2 * Math.PI * i) / n, r = R * v / 100; return [cx + r * Math.cos(a), cy + r * Math.sin(a)]; };
  const poly = k => COMP.map((c, i) => pt(i, c[k]).join(",")).join(" ");
  return (
    <svg viewBox="-16 0 252 220" width="100%" style={{ maxWidth: 290 }} role="img" aria-label="Competency radar: candidate against target">
      {[25, 50, 75, 100].map(g => <polygon key={g} points={COMP.map((_, i) => pt(i, g).join(",")).join(" ")} fill="none" stroke="#dbe2ea" strokeWidth="1"/>)}
      {COMP.map((_, i) => <line key={i} x1={cx} y1={cy} x2={pt(i, 100)[0]} y2={pt(i, 100)[1]} stroke="#e3e9f0" strokeWidth="1"/>)}
      <polygon points={poly(2)} fill="rgba(185,130,46,.12)" stroke="#b9822e" strokeWidth="1.6" strokeDasharray="4 3"/>
      <polygon points={poly(1)} fill="rgba(14,159,142,.22)" stroke="#0e9f8e" strokeWidth="2"/>
      {COMP.map((c, i) => { const [x, y] = pt(i, 118); return <text key={c[0]} x={x} y={y + 3} textAnchor="middle" style={{ fontSize: 7.4, fontWeight: 700, fill: T.ink2 }}>{c[0].split(" ")[0]}</text>; })}
    </svg>
  );
}
function CompetencyDashboard() {
  const gaps = COMP.filter(c => c[1] < c[2]).sort((a, b) => (b[2] - b[1]) - (a[2] - a[1])).slice(0, 3);
  return (
    <div className="glass" style={{ padding: 20 }} role="group" aria-label="Sample competency assessment dashboard (illustrative data)">
      <Head eyebrow="Competency assessment" title="Competency Dashboard" who="Emily Carter · Manager – Operations · vs target profile"/>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }} className="grid2">
        <Panel>
          <Sub>Profile vs role target</Sub>
          <div style={{ display: "flex", justifyContent: "center" }}><Radar/></div>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", fontSize: 10, color: T.mute, marginTop: 4 }}>
            <span><i style={{ display: "inline-block", width: 10, height: 3, background: "#0e9f8e", marginRight: 5, verticalAlign: "middle" }}/>Candidate</span>
            <span><i style={{ display: "inline-block", width: 10, height: 0, borderTop: "2px dashed #b9822e", marginRight: 5, verticalAlign: "middle" }}/>Target</span>
          </div>
        </Panel>
        <div style={{ display: "grid", gap: 12 }}>
          <Panel>
            <Sub>Role fit</Sub>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span className="serif" style={{ fontSize: 40, fontWeight: 700, color: "#0e9f8e", lineHeight: 1 }}>82</span>
              <span style={{ fontSize: 11, color: T.mute }}>overall index · 5 of 8 competencies at or above target</span>
            </div>
          </Panel>
          <Panel>
            <Sub>Largest gaps to target</Sub>
            <div style={{ display: "grid", gap: 8 }}>
              {gaps.map(([n, v, t]) => (
                <div key={n}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontWeight: 600, color: T.ink2, marginBottom: 3 }}><span>{n}</span><span style={{ color: "#d9483f", fontWeight: 800 }}>−{t - v}</span></div>
                  <div style={{ position: "relative", height: 7, borderRadius: 99, background: "#e8edf3" }}>
                    <div style={{ width: `${v}%`, height: "100%", borderRadius: 99, background: "linear-gradient(90deg,#1f63c6,#5b93e6)" }}/>
                    <div style={{ position: "absolute", left: `${t}%`, top: -3, width: 2, height: 13, background: "#b9822e", borderRadius: 1 }}/>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
      <Panel>
        <Sub>Top competencies</Sub>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 18px" }} className="grid2">
          {[...COMP].sort((a, b) => b[1] - a[1]).slice(0, 4).map(([n, v], i) => (
            <div key={n} style={{ display: "grid", gridTemplateColumns: "92px 1fr 24px", gap: 8, alignItems: "center", fontSize: 11, color: T.ink2 }}>
              <span>{n}</span>
              <div style={{ height: 6, borderRadius: 99, background: "#e8edf3" }}><div style={{ width: `${v}%`, height: "100%", borderRadius: 99, background: ["linear-gradient(90deg,#6d2fd0,#9b6bff)", "linear-gradient(90deg,#b9822e,#d9b36f)", "linear-gradient(90deg,#0e9f8e,#4cc7b8)", "linear-gradient(90deg,#1f63c6,#5b93e6)"][i] }}/></div>
              <b style={{ textAlign: "right" }}>{v}</b>
            </div>
          ))}
        </div>
      </Panel>
      <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
        {["Competency map", "9-Box", "Development plan (IDP)", "Succession"].map(r => <span key={r} style={{ fontSize: 9.5, fontWeight: 700, padding: "4px 11px", borderRadius: 8, background: T.ink, color: "#fff" }}>{r}</span>)}
      </div>
    </div>
  );
}

export default function DashboardShowcase() {
  return (
    <div className="grid2" style={{ alignItems: "start" }}>
      <CompetencyDashboard/>
      <DiscDashboard/>
    </div>
  );
}
